<template>
  <div>
    <StaticDataTable
      class="static-table-list"
      :editing="editingOrder"
      :hover="true"
      :striped="false"
      @sortUpdated="sortUpdated($event)"
      @sortStarted="sortStarted($event)"
      :key="updateKey">
      <template v-slot:header>
        <tr>
          <th width="25px"></th>
          <th width="25px" v-if="editingOrder"></th>
          <th width="25px"></th>
          <th width="120px">Type</th>
          <th width="120px" v-if="showStatus">Status</th>
          <th width="20%">Name</th>
          <th width="30%">{{isLocationTrackingEnabled ? 'Location' : 'Info'}}</th>
          <th width="10%">Other Info</th>
          <th width="10%">Instructions</th>
          <th width="10%"><Tooltip label="Estimated duration, not including travel time" helpStyle>Duration</Tooltip></th>
          <th width="10%" v-if="isOperationalReportingEnabled"><Tooltip label="Operational Reporting details per order" helpStyle>Operational</Tooltip></th>
          <th width="50px" v-if="(showActions || showOrderActions)">Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <template v-for="(taskItem, taskItemIndex) in taskItems">
          <tr :key="`ti-${taskItem.id}-${taskItem.key}`" :data-sort-id="taskItem.id" class="static-table-sortable">
            <td class="tasks-item-row-index-marker">{{taskItemIndex+1}}</td>
            <td v-if="editingOrder">
              <div class="static-datatable-row-draggable"><i class="fal fa-bars"></i> </div>
            </td>
            <td>
              <ExpandCollapseButton v-model="taskItem.expanded" @update="updateTaskItemExpanded(taskItem, $event)" v-if="canShowTaskItemExpand(taskItem)" />
            </td>
            <td>
              <LabelBadge :labelClass="`label-taskitem-type-${taskItem.type}`">{{taskItem.type | formatLookupName(TaskItemTypeLookup)}}</LabelBadge>
            </td>
            <td v-if="showStatus">
              <LabelBadge :labelClass="`label-taskitem-status-${taskItem.status}`">{{taskItem.status | formatLookupName(TaskItemStatusTypeLookup)}}</LabelBadge>
            </td>
            <td class="font-weight-bold">
              <span v-if="!taskItem.editingName">{{taskItem.name}}</span>
              <FormInputText v-if="taskItem.editingName" v-model="taskItem.name" @input="taskItemUpdated(taskItem)" />

              <Tooltip label="From existing task"><i class="far fa-info-circle text-muted ml-2" v-if="isMergingTasks && taskItem.fromMergedTask"></i></Tooltip>
              <Tooltip v-if="taskItemHasWarnings(taskItem)" :label="(taskItem.warningList || []).join(', ')"><i class="far fa-exclamation-triangle text-danger ml-2"></i></Tooltip>
              <a v-if="editing && !taskItem.editingName" class="table-item-editable-button ml-2" @click.prevent="taskItem.editingName = true"><i class="far fa-pencil"></i></a>
            </td>
            <td class="font-weight-bold">
              <span v-if="!taskItem.editingLocation">{{isLocationTrackingEnabled ? taskItem.locationAddress : '-'}}</span>
              <FormInputAddressSearch v-if="taskItem.editingLocation" v-model="taskItem.locationAddress" @input="taskItemUpdated(taskItem)" :splitAddressComponents="false" />
              <a v-if="editing && !taskItem.editingLocation && isLocationTrackingEnabled && taskItem.isLocationEnabled" class="table-item-editable-button ml-2" @click.prevent="taskItem.editingLocation = true"><i class="far fa-pencil"></i></a>
            </td>
            <td>
              {{formatHyphenEmptyValue(taskItemOtherInfo(taskItem))}}
            </td>
            <td>
              <span v-if="!taskItem.editingDetails">{{formatHyphenEmptyValue(taskItem.details)}}</span>
              <FormInputText v-if="taskItem.editingDetails" v-model="taskItem.details" @input="taskItemUpdated(taskItem)" />
              <a v-if="editing && !taskItem.editingDetails" class="table-item-editable-button ml-2" @click.prevent="taskItem.editingDetails = true"><i class="far fa-pencil"></i></a>
            </td>
            <td>
              <span v-if="!taskItem.editingDuration">{{formatDuration(taskItem.estimatedProcessingTime * 60, { hideSeconds: true })}}</span>
              <FormInputText v-if="taskItem.editingDuration" type="number" v-model="taskItem.estimatedProcessingTime" @input="taskItemUpdated(taskItem)" addon="mins" />
              <a v-if="editing && !taskItem.editingDuration" class="table-item-editable-button ml-2" @click.prevent="taskItem.editingDuration = true"><i class="far fa-pencil"></i></a>
            </td>
            <td v-if="isOperationalReportingEnabled">
              <LabelBadge labelClass="label-status-pending" v-if="taskItemOperationalReportingRequiredCount(taskItem) > 0">{{taskItemOperationalReportingRequiredCount(taskItem)}} Required</LabelBadge>
              <span v-else-if="isTaskItemOperationCodeEnabled(taskType, taskItem)"><i class="far fa-check-circle result-fg-success"></i></span>
              <span v-else>-</span>
            </td>
            <td v-if="(showActions || showOrderActions)">
              <ActionDropMenu :tableAction="true">
                <DropdownMenuItem @click="completeTaskItem(taskItem.id)" icon="fa-check-circle" v-if="showCompleteTaskItem(taskItem)">Complete</DropdownMenuItem>
                <DropdownMenuDivider v-if="showCompleteTaskItem(taskItem) && showActions" />
                <DropdownMenuItem @click="editTaskItem(taskItem.id)" icon="fa-pencil" v-if="showActions && isTaskItemEditable(taskItem)">Edit Item</DropdownMenuItem>
                <DropdownMenuItem @click="cancelTaskItem(taskItem.id)" icon="fa-trash-alt" v-if="showActions && isTaskItemEditable(taskItem)">Cancel Item</DropdownMenuItem>
                <DropdownMenuItem @click="removeTaskItem(taskItem.id)" icon="fa-trash-alt" v-if="showOrderActions && !taskItem.fromMergedTask">Remove</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>

          <template v-if="taskItem.expanded">
            <tr v-for="linkedItem of taskItem.taskItemLinkedItems" :key="`linkedItem-${linkedItem.id}-${taskItem.key}-${linkedItem.operationalReportingCode}`">
              <td></td>
              <td v-if="editingOrder"></td>
              <td :colspan="editing ? 2 : (2 + (showStatus ? 1 : 0))">
                <i class="fas fa-caret-right mr-2"></i>
                {{formatLookupName(linkedItem.type, TaskLinkedItemTypeLookup)}}
                <span v-if="linkedItem.order != null"> - </span>
                <LinkOrder v-if="linkedItem.order != null && !linkedItem.purchaseOrder" :id="linkedItem.order.id" :name="linkedItem.order.sequenceNumber" />
                <LinkPurchaseOrder v-if="linkedItem.purchaseOrder != null" :id="linkedItem.purchaseOrder.id" :name="linkedItem.purchaseOrder.sequenceNumber" />
                <Tooltip label="From existing task"><i class="far fa-info-circle text-muted ml-2" v-if="isMergingTasks && linkedItem.fromMergedTask"></i></Tooltip>
                <Tooltip label="Order was charged a per-task service fee item for this task"><i class="far fa-usd-circle text-muted ml-2" v-if="linkedItem.hasOrderServiceChargeApplied"></i></Tooltip>
              </td>
              <td>
                <LinkContact v-if="linkedItem.order != null" :id="linkedItem.order.contact.id" :name="linkedItem.order.contact.name" />
                <span v-if="isStockTransfer(taskItem, linkedItem)">
                  <LinkStock :id="linkedItem.stock.id" :name="linkedItem.stock.name" />
                </span>
              </td>
              <td colspan="2">
                <LabelBadge labelClass="label-task-loaded-onsite mr-1" v-if="linkedItem.isLoadedAtLocation">Loaded Onsite</LabelBadge>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.orderItem">
                  {{linkedItem.orderItem.quantity}}x {{linkedItem.orderItem.description}} {{linkedItem.orderItem.generatedDescription}}<span v-if="isOrderItemGlass(linkedItem.orderItem)"> - {{linkedItem.orderItem.glassHeight}}x{{linkedItem.orderItem.glassWidth}}</span>
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.productionPanel">
                  {{linkedItem.productionPanel.barcode}} - {{linkedItem.productionPanel.panelHeight}} x {{linkedItem.productionPanel.panelWidth}} - {{linkedItem.orderItem.description}} {{linkedItem.orderItem.generatedDescription}}
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.general">
                  <UOMValue :value="linkedItem.quantity" :uomType="linkedItem.stock.uomType" />
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.glassLevel">
                  {{linkedItem.quantity}}x {{linkedItem.glassLevel.height}} x {{linkedItem.glassLevel.width}}
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.purchaseOrderItem">
                  {{linkedItem.purchaseOrderItem.quantity}}x {{linkedItem.purchaseOrderItem.description}} {{linkedItem.purchaseOrderItem.generatedDescription}}<span v-if="isPurchaseOrderItemGlass(linkedItem.purchaseOrderItem)"> - {{linkedItem.purchaseOrderItem.glassHeight}}x{{linkedItem.purchaseOrderItem.glassWidth}}</span>
                </span>
              </td>
              <td colspan="2">
                <span v-if="(linkedItem.type == TaskLinkedItemTypes.orderItem || linkedItem.type == TaskLinkedItemTypes.order) && linkedItem.order">
                  {{linkedItem.order.deliveryInstructions}}
                </span>
              </td>
              <td v-if="isOperationalReportingEnabled">
                <div v-if="isOperationalCodeDisplayed(taskType, taskItem, linkedItem)">
                  <LabelBadge :class="`task-operational-category-${taskOpCodeCategoryLookup(linkedItem.operationalReportingCode)}`">{{formatLookupName(linkedItem.operationalReportingCode, ItemOperationalReportingCodeLookup)}}</LabelBadge>
                  <a class="table-item-editable-button ml-2" v-if="canEditTaskItem(taskItem) && editing" @click.prevent="displayOperationalCodeEdit($event, taskItem, linkedItem)"><i class="far fa-pencil"></i></a>
                </div>
              </td>
              <td v-if="(showActions || showOrderActions)">
                <ActionDropMenu :tableAction="true">
                  <DropdownMenuItem @click="removeLinkedItem(taskItem.id, linkedItem.id)" icon="fa-trash-alt" v-if="showOrderActions && !linkedItem.fromMergedTask">Remove</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>

        </template>
      </template>
    </StaticDataTable>

    <TaskOperationalCodePopover
      ref="operationalCodeEditPopover"
      :order="operationalCodePopoverInfo.order"
      :taskScheduleStartDate="task.scheduleStartDate"
      :codeValue="operationalCodePopoverInfo.linkedItem ? operationalCodePopoverInfo.linkedItem.operationalReportingCode : null"
      :codeNote="operationalCodePopoverInfo.linkedItem ? operationalCodePopoverInfo.linkedItem.operationalReportingNote : null"
      @update:codeValue="updateOpCodeValue($event)"
      @update:codeNote="updateOpCodeNote($event)"
      @resetCode="resetOpCode()"
    />

  </div>
</template>



<script>
import _ from 'lodash'
import moment from 'moment'

import TaskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'
import Popover from '@/components/popover.vue'
import TaskOperationalCodePopover from '@/modules/tasks/components/taskOperationalCodePopover'

import { TaskItemTypes, TaskItemTypeLookup, TaskStatusTypes, TaskStatusTypeLookup, TaskItemStatusTypes, TaskItemStatusTypeLookup, InventoryStockTypes, TaskLinkedItemTypes, TaskLinkedItemTypeLookup } from '@/store/types'
import { ItemOperationalReportingCodes, ItemOperationalReportingCodeLookup } from '@/store/types'

export default {
  name: 'TaskItemList',
  model: {
    event: 'update'
  },
  mixins: [
    TaskDespatchMixin
  ],
  components: {
    Popover,
    TaskOperationalCodePopover
  },
  props: {
    task: Object,
    taskType: Object,
    value: Array,

    defaultExpanded: {
      type: Boolean,
      default: false
    },

    showStatus: Boolean,
    showActions: Boolean,
    showOrderActions: Boolean,
    editingOrder: Boolean,
    editing: Boolean,
    isMergingTasks: Boolean
  },
  computed: {
    isLocationTrackingEnabled() {
      return this.taskType.isLocationTrackingEnabled
    },
    isOperationalReportingEnabled() {
      return this.taskType.isOperationalReportingEnabled
    }
  },
  data() {
    return {
      updateKey: 1,
      taskItems: [],

      operationalCodePopoverInfo: {
        taskItem: null,
        linkedItem: null,
        order: null
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: function() {
        this.taskItems = (this.value || []).map(taskItem => {
          var outputItem = _.cloneDeep(taskItem)
          
          outputItem.expanded = !!outputItem.expanded
          outputItem.editingName = !!outputItem.editingName && this.editing
          outputItem.editingLocation = !!outputItem.editingLocation && this.editing
          outputItem.editingDetails = !!outputItem.editingDetails && this.editing
          outputItem.editingDuration = !!outputItem.editingDuration && this.editing

          return outputItem
        })
      }
    },
    editing: function() {
      // Collapse all items if editing started
      if(this.editing) {
        this.taskItems.forEach(taskItem => {
          taskItem.expanded = false
        })
      }
    }
  },
  created() {
    this.TaskItemTypeLookup = TaskItemTypeLookup
    this.TaskStatusTypeLookup = TaskStatusTypeLookup
    this.TaskItemStatusTypeLookup = TaskItemStatusTypeLookup
    this.TaskLinkedItemTypes = TaskLinkedItemTypes
    this.TaskLinkedItemTypeLookup = TaskLinkedItemTypeLookup
    this.ItemOperationalReportingCodeLookup = ItemOperationalReportingCodeLookup
  },
  mounted() {
  },
  methods: {
    sortUpdated(list) {
      this.taskItems = _.compact(list.map(sortKey => {
        return this.taskItems.find(taskItem => taskItem.id == sortKey)
      }))
      this.$emit('update', this.taskItems)
      this.$emit('sortUpdated', list)
    },
    sortStarted(list) {
      // Collapse all
      this.taskItems.forEach(taskItem => {
        taskItem.expanded = false
      })
      this.$emit('update', this.taskItems)
    },
    editTaskItem(taskItemId) {
      this.$emit('editTaskItem', taskItemId)
    },
    cancelTaskItem(taskItemId) {
      this.$emit('cancelTaskItem', taskItemId)
    },
    removeLinkedItem(taskItemId, linkedItemId) {
      this.$emit('removeTaskItemLinkedItem', { taskItemId: taskItemId, taskItemLinkedItemId: linkedItemId })
    },
    removeTaskItem(taskItemId) {
      this.$emit('removeTaskItem', taskItemId)
    },
    updateTaskItemExpanded(taskItem, expanded) {
      if(expanded)
        this.$emit('expandTaskItem', taskItem.id)
      else
        this.$emit('collapseTaskItem', taskItem.id)
    },
    taskItemUpdated(taskItem) {
      if(this.editing)
        this.$emit('update', this.taskItems)
    },
    isTaskItemCancelled(taskItem) {
      return taskItem.status == TaskItemStatusTypes.cancelled
    },
    taskItemHasWarnings(taskItem) {
      return taskItem.warningList && taskItem.warningList.length > 0
    },
    canEditTaskItem(taskItem) {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
        
      if(taskItem.status == TaskItemStatusTypes.completed || taskItem.status == TaskItemStatusTypes.cancelled)
        return false
      if(this.task.status == TaskStatusTypes.completed || this.task.status == TaskStatusTypes.cancelled)
        return false
      return true
    },
    showCompleteTaskItem(taskItem) {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      if(!this.showActions)
        return false
      if(taskItem.status == TaskItemStatusTypes.completed || taskItem.status == TaskItemStatusTypes.cancelled)
        return false
      if(this.task.status == TaskStatusTypes.completed || this.task.status == TaskStatusTypes.cancelled)
        return false
      if(this.task.status == TaskStatusTypes.pendingProduction || this.task.status == TaskStatusTypes.pendingApproval)
        return false

      return true
    },
    isTaskItemEditable(taskItem) {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      if(!this.showActions)
        return false
      if(taskItem.status == TaskItemStatusTypes.completed || taskItem.status == TaskItemStatusTypes.cancelled)
        return false
      if(this.task.status == TaskStatusTypes.completed || this.task.status == TaskStatusTypes.cancelled)
        return false

      return true
    },
    isOrderItemGlass(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass
    },
    isPurchaseOrderItemGlass(purchaseOrderItem) {
      return purchaseOrderItem.stockType == InventoryStockTypes.glass
    },
    completeTaskItem(taskItemId) {
      this.$emit('completeTaskItem', taskItemId)
    },
    taskItemOtherInfo(taskItem) {
      var description = ''

      if(taskItem.type == TaskItemTypes.orderDespatch) {
        var orderItemQuantity = 0

        var orderIdList = []

        taskItem.taskItemLinkedItems.forEach(linkItem => {
          orderIdList.push(linkItem.orderId)
          if(linkItem.type == TaskLinkedItemTypes.orderItem)
            orderItemQuantity += linkItem.quantity
        })

        orderIdList = _.uniq(orderIdList);

        description = `${orderIdList.length} order${this.displayCountPlural(orderIdList.length)}`

        if(orderItemQuantity > 0) {
          var totalUnits = orderItemQuantity
          description += `, ${totalUnits} item${this.displayCountPlural(totalUnits)}`
        }
      }

      return description
    },
    canShowTaskItemExpand(taskItem) {
      if(!taskItem.taskItemLinkedItems)
        return false
      return taskItem.taskItemLinkedItems.length > 0
    },
    isStockTransfer(taskItem, linkedItem) {
      if(taskItem.type == TaskItemTypes.transferInternal || taskItem.type == TaskItemTypes.transferExternal) {
        return linkedItem.type == TaskLinkedItemTypes.general || linkedItem.type == TaskLinkedItemTypes.glassLevel
      }
      return false
    },
    

    taskItemOperationalReportingRequiredCount(taskItem) {
      if(!this.isTaskItemOperationCodeEnabled(this.taskType, taskItem))
        return 0

      var validLinkedItems = taskItem.taskItemLinkedItems.filter(li => (li.type == TaskLinkedItemTypes.order || li.type == TaskLinkedItemTypes.orderItem) && li.orderId)

      // Make sure the 1st linked item for each order has an op code set
      var orderIds = _.uniq(validLinkedItems.map(li => li.orderId))

      var reportingRequiredCount = orderIds.reduce((count, orderId) => {
        var linkedItemsForOrder = validLinkedItems.filter(li => li.orderId == orderId)
        if(linkedItemsForOrder.length == 0)
          return count

        var firstLinkedItemForOrder = linkedItemsForOrder[0]
        if(firstLinkedItemForOrder.operationalReportingCode == null || firstLinkedItemForOrder.operationalReportingCode == ItemOperationalReportingCodes.none)
          return count + 1
        return count
      }, 0)

      return reportingRequiredCount
    },
    displayOperationalCodeEdit(evt, taskItem, linkedItem) {
      this.$refs.operationalCodeEditPopover.displayFromElement(evt.target)

      this.operationalCodePopoverInfo.taskItem = taskItem
      this.operationalCodePopoverInfo.linkedItem = linkedItem
      this.operationalCodePopoverInfo.order = linkedItem.order
    },
    updateOpCodeValue(value) {
      this.operationalCodePopoverInfo.linkedItem.operationalReportingCode = value

      let selTaskItem = this.taskItems.find(ti => ti.id == this.operationalCodePopoverInfo.taskItem.id)
      if(selTaskItem) {
        selTaskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId == this.operationalCodePopoverInfo.linkedItem.orderId) {
            linkedItem.operationalReportingCode = value
          }
        })

        this.taskItemUpdated(selTaskItem)
      }
    },
    updateOpCodeNote(note) {
      this.operationalCodePopoverInfo.linkedItem.operationalReportingNote = note

      let selTaskItem = this.taskItems.find(ti => ti.id == this.operationalCodePopoverInfo.taskItem.id)
      if(selTaskItem) {
        selTaskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId == this.operationalCodePopoverInfo.linkedItem.orderId) {
            linkedItem.operationalReportingNote = note
          }
        })

        this.taskItemUpdated(selTaskItem)
      }
    },
    resetOpCode() {
      // Reset back to calculated code
      var opCodeInfo = this.calculateOperationalInfo(this.task, [], TaskLinkedItemTypes.order, this.operationalCodePopoverInfo.order)

      if(!this.operationalCodePopoverInfo.taskItem)
        return

      this.operationalCodePopoverInfo.linkedItem.operationalReportingCode = opCodeInfo.code

      let selTaskItem = this.taskItems.find(ti => ti.id == this.operationalCodePopoverInfo.taskItem.id)
      if(selTaskItem) {
        selTaskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId == this.operationalCodePopoverInfo.linkedItem.orderId) {
            linkedItem.operationalReportingCode = opCodeInfo.code
          }
        })

        this.taskItemUpdated(selTaskItem)
      }
    }
  }
}
</script>